import { ReactElement } from "react";
import { Location } from "react-router-dom";
import { FormFieldType, GroupFieldType } from "shared/types";
import { FormFieldEnums } from "shared/types/enums";
import { Account } from "shared/types/UserDetailsTypes";
import { AccountLicenseEnum } from "shared/types/enums/AccountLicenseEnum";
import { Brand } from "shared/constants/brands";
import { HostType } from "shared/types/HostsType";

export const findStepIndexFromURL = (
  location: Location,
  steps: { name: string; element: ReactElement }[]
): number => {
  const findStep = steps.find(
    (step) =>
      step.name ===
      location.pathname.slice(
        location.pathname.lastIndexOf("/") + 1,
        location.pathname.length
      )
  );

  if (findStep) {
    return steps.indexOf(findStep);
  } else {
    return 0;
  }
};

export const isSkippableStep = (step: {
  title: string;
  element: ReactElement<{ fields: FormFieldType[] }>;
}) => {
  const flattenFields = (fields: FormFieldType[]): FormFieldType[] =>
    fields.flatMap((field: FormFieldType) =>
      field.type == FormFieldEnums.Group ? flattenFields(field.fields) : field
    );

  const allFields = flattenFields(step.element.props.fields);

  return !allFields.some(
    (field) => "required" in field && field.required === true
  );
};

export function isStepSkippableCheck(fields: FormFieldType[]): boolean {
  const hasRequiredField = (fields: FormFieldType[]): boolean => {
    for (const field of fields) {
      if (field.type === "group" && "fields" in field) {
        if (hasRequiredField((field as GroupFieldType).fields)) {
          return true;
        }
      } else if ("required" in field && field.required) {
        return true;
      }
    }
    return false;
  };

  return !hasRequiredField(fields);
}

export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const createAccountName = (account: Account) => {
  const license = AccountLicenseEnum[account.License];
  const currency = account.Currency;
  const type = account.IsDemo ? "Demo" : "Real";

  return `${license} | ${currency} | ${type}`;
};

export const colorByAccountType = (
  type: AccountLicenseEnum,
  isDemo: boolean
) => {
  switch (type) {
    case AccountLicenseEnum.Forex:
      if (isDemo) {
        return "#6FC7C1";
      }
      return "#0D6E67";
    case AccountLicenseEnum.SingleStocks:
      if (isDemo) {
        return "#5C6FFF";
      }
      return "#10376D";
    default:
      return "#fff";
  }
};

export function getEnvAndBrand() {
  const { hostname } = window.location;

  // Split on dots
  const hostParts = hostname.split(".");

  // The subdomain part is the first piece, e.g. "staging-draco-might"
  const subdomain = hostParts[0];
  const subdomainParts = subdomain.split("-");

  // The environment is always the first segment
  let environment = subdomainParts[0];

  const brand = subdomainParts.slice(1).join("-") as Brand;

  if (environment === "prod") {
    environment = "production";
  }

  return { environment, brand };
}

export function getUrlFromRedirect(input: string): string {
  // Remove the literal "redirect$-$" from the start of the string
  return input.replace(/^redirect\$-\$/, "");
}

export function getHostByUrl(url: string): HostType | null {
  if (
    url.includes("semipro") ||
    url.includes("pro") ||
    url.includes("ready") ||
    url.includes("readyst")
  ) {
    return "pro";
  }
  return "web";
}


export function getLocalStorage(name: string) {
  try {
    if (!window.parent) {
      return null;
    }

    const result = window.parent.localStorage.getItem(name);
    return result && result.length ? result : null;
  } catch (e) {
    console.log("not support localStorage" + e);
    return null;
  }
}

export const GetMarketingInfo = (): {
  screenSize: string;
  timeZone: number;
  timeZoneName: string;
  newDevice: string;
} => {
  const screenSize = `${window.screen.width}X${window.screen.height}`;

  const timeZone = new Date().getTimezoneOffset() / -60;

  const timeZoneName =
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? "Unknown";

  const newDevice =
    window.localStorage.getItem("LcND") ??
    (() => {
      const id = crypto.randomUUID();
      window.localStorage.setItem("LcND", id);
      return id;
    })();

  return {
    screenSize,
    timeZone,
    timeZoneName,
    newDevice,
  };
};
